import { DocumentTypeDecoration } from '@graphql-typed-document-node/core'

export type ResponseError = string[] | string | { message: string | string[] }

export type CommonPaginator = {
  current?: number | null | undefined
  totalCount?: number | null | undefined
  endPage?: number | null | undefined
} | null

// Common List of entities response
export type EntityListResponse<TItem> = {
  [key: string]: {
    items?: TItem[] | null
    paginator?: CommonPaginator | null
  } | null
} | null

export type ItemsOfEntityListResponse<TResponse> = TResponse extends EntityListResponse<infer TItem> ? TItem[] : never

// Common Mutation or Single entity response
export type EntityResponse<TData> = {
  [key: string]: {
    data?: TData | null
    errors?: ResponseError | null
  } | null
} | null

export type ItemOfEntityResponse<TResponse> = TResponse extends EntityResponse<infer TItem> ? TItem : never

// EntityListResponse<TData> -> TData[]
export const processEntityListResponse = <TData>(response: EntityListResponse<TData>): TData[] => {
  if (!response) {
    return []
  }

  const keys = Object.keys(response)
  if (keys.length === 0) {
    return []
  }

  const firstKey = keys[0]
  const entityData = response[firstKey]

  if (!entityData || !entityData.items) {
    return []
  }

  // Handle case where items might be a single item instead of array
  return Array.isArray(entityData.items) ? entityData.items : [entityData.items]
}

export const isEntityListResponse = <TData>(response: RawResponse<TData>): response is EntityListResponse<TData> => {
  if (typeof response !== 'object' || response === null) {
    return false
  }

  const keys = Object.keys(response)
  if (keys.length === 0) {
    return false
  }

  // Check if it's not a server exception response
  if ('errors' in response) {
    return false
  }

  const firstKey = keys[0]
  const firstValue = response[firstKey]

  // Check if the first value exists and has the expected structure
  return firstValue !== null && typeof firstValue === 'object' && 'items' in firstValue
}

export const processEntityResponse = <TData>(response: EntityResponse<TData>): TData => {
  if (!response) {
    throw new Error('Response is null or undefined')
  }

  const keys = Object.keys(response)
  if (keys.length === 0) {
    throw new Error('Response has no keys')
  }

  const firstKey = keys[0]
  const entityData = response[firstKey]

  if (!entityData || !entityData.data) {
    return null
  }

  return entityData.data
}

export const isEntityResponse = <TData>(response: RawResponse<TData>): response is EntityResponse<TData> => {
  if (typeof response !== 'object' || response === null) {
    return false
  }

  const keys = Object.keys(response)
  if (keys.length === 0) {
    return false
  }

  const firstKey = keys[0]
  const firstValue = response[firstKey]

  // Check if the first value exists and has the expected structure
  return firstValue !== null && typeof firstValue === 'object' && 'data' in firstValue
}

const errorKeyHasErrors = (errorValue: ResponseError) => {
  // { errors: ['There', 'is', 'an', 'error'] }
  if (Array.isArray(errorValue)) {
    return errorValue.length > 0
  }

  // { errors: "There is an error" }
  if (typeof errorValue === 'string') {
    return true
  }

  // { errors: { message: "There is an error" } }
  if (typeof errorValue === 'object' && 'message' in errorValue) {
    return typeof errorValue.message === 'string' && errorValue.message.length > 0
  }

  return false
}
export const isErrorResponse = <TData>(response: RawResponse<TData>): boolean => {
  if (typeof response !== 'object' || response === null) {
    return false
  }

  const keys = Object.keys(response)
  if (keys.length === 0) {
    return false
  }

  const firstKey = keys[0]
  const actualResponse = response[firstKey]

  return (
    actualResponse !== null &&
    typeof actualResponse === 'object' &&
    'errors' in actualResponse &&
    errorKeyHasErrors(actualResponse.errors)
  )
}

// Type of every possible response from the server
export type RawResponse<TData> = EntityListResponse<TData> | EntityResponse<TData>

export const isRawResponse = <TData>(response: unknown): response is RawResponse<TData> => {
  return typeof response === 'object' && response !== null && Object.keys(response).length > 0
}

// IDK if something like this is exists already, but is is pretty useful thing
// it is an exact copy of FragmentType but instead of fragment reference it returns actual value which this fragment is defining
export type FragmentValue<TDocumentType extends DocumentTypeDecoration<any, any>> =
  TDocumentType extends DocumentTypeDecoration<infer TType, any>
    ? [TType] extends [{ ' $fragmentName'?: infer TKey }]
      ? TKey extends string
        ? TType
        : never
      : never
    : never
